<template>
    <div class="form-overlay select-variable" :class="position">

        <div class="steep1" v-if="view === 'list'">
            <div class="content searchVariable">
                <div class="input">
                    <i class="fa-regular fa-magnifying-glass"></i>
                    <input :placeholder="$t('dash.posts.variables.search-placeholder')" v-model="textSearch" ref="inputSearch">
                </div>
                <div class="list-item hide-scrollbar">
                    <template v-if="filteredVariables.length">
                        <div class="item" v-for="variable in filteredVariables" @click="selectVariable(variable)" :key="variable.id">
                            <div class="icon">
                                <i class="fa-solid fa-code"></i>
                            </div>
                            <div class="info">
                                <p class="name">{{ variable.name }}</p>
                            </div>
                            <i class="fa-solid fa-pencil action-icon" @click.stop="openEditForm(variable)"></i>
                            <i class="fa-solid fa-xmark action-icon" @click.stop="onDelete(variable)"></i>
                        </div>
                    </template>
                    <p v-else class="no-result">{{ $t('dash.posts.variables.no-result') }}</p>
                </div>
                <a class="manage-link-text" href="/#/dash/settings/variables" target="_blank" @click.stop>
                    {{ $t('dash.posts.variables.manage-link') }}
                </a>
            </div>
        </div>

        <div class="steep1" v-else-if="view === 'create'">
            <div class="content createVariable">
                <div class="back" @click="backToList">
                    <i class="fa-solid fa-arrow-left"></i>
                    {{ $t('dash.posts.labels.back') }}
                </div>
                <div class="input">
                    <input
                        type="text"
                        v-model="newVariableName"
                        :placeholder="$t('dash.settings.variables.name-placeholder')"
                        @keyup.enter="submitVariable"
                        ref="inputCreate"
                    >
                </div>
                <p class="form-error" v-if="formError">{{ formError }}</p>
            </div>
        </div>

        <div class="steep1" v-else-if="view === 'values'">
            <div class="content valuesVariable">
                <div class="back" @click="backToList">
                    <i class="fa-solid fa-arrow-left"></i>
                    {{ $t('dash.posts.labels.back') }}
                </div>
                <p class="values-hint">
                    {{ $t('dash.posts.variables.values-hint') }} <strong>{{ selectedVariable && selectedVariable.name }}</strong>
                </p>
                <div class="list-item hide-scrollbar">
                    <div class="value-row" v-for="row in valuesDraft" :key="row.page.id">
                        <svg v-if="row.page.network === 'bluesky'" class="network-icon bluesky" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 640"><path d="M439.8 358.7C436.5 358.3 433.1 357.9 429.8 357.4C433.2 357.8 436.5 358.3 439.8 358.7zM320 291.1C293.9 240.4 222.9 145.9 156.9 99.3C93.6 54.6 69.5 62.3 53.6 69.5C35.3 77.8 32 105.9 32 122.4C32 138.9 41.1 258 47 277.9C66.5 343.6 136.1 365.8 200.2 358.6C203.5 358.1 206.8 357.7 210.2 357.2C206.9 357.7 203.6 358.2 200.2 358.6C106.3 372.6 22.9 406.8 132.3 528.5C252.6 653.1 297.1 501.8 320 425.1C342.9 501.8 369.2 647.6 505.6 528.5C608 425.1 533.7 372.5 439.8 358.6C436.5 358.2 433.1 357.8 429.8 357.3C433.2 357.7 436.5 358.2 439.8 358.6C503.9 365.7 573.4 343.5 593 277.9C598.9 258 608 139 608 122.4C608 105.8 604.7 77.7 586.4 69.5C570.6 62.4 546.4 54.6 483.2 99.3C417.1 145.9 346.1 240.4 320 291.1z"/></svg>
                        <i v-else class="network-icon fa-brands" :class="`fa-${row.page.network}`"></i>
                        <span class="page-name">{{ row.page.name }}</span>
                        <input type="text" v-model="row.value" :placeholder="$t('dash.settings.variables.value-placeholder')">
                        <i
                            v-if="hasAnyMissingValue && row.value"
                            class="fa-solid fa-clone duplicate-icon"
                            :title="$t('dash.settings.variables.bulk-apply-tooltip')"
                            @click="openBulkApplyFromRow(row)"
                        ></i>
                    </div>
                </div>
            </div>
        </div>

        <BulkApplyVariableValue
            v-if="bulkApply"
            :variable="selectedVariable"
            :all-pages="Object.values(allPages)"
            :initial-value="bulkApply.value"
            :initial-selected-page-ids="bulkApply.defaultPageIds"
            @close="bulkApply = null"
            @applied="onBulkApplied"
        />

        <div class="btns">
            <template v-if="view === 'list'">
                <div class="btn-secondary" @click="openCreateForm">{{ $t('dash.settings.variables.add-button') }}</div>
                <div class="btn-secondary" @click="close">{{ $t('dash.button.close') }}</div>
            </template>
            <template v-else-if="view === 'create'">
                <div class="btn-secondary" :class="{disable: !newVariableName.trim() || submitting}" @click="submitVariable">
                    {{ editingVariableId ? $t('dash.settings.variables.save') : $t('dash.settings.variables.create') }}
                </div>
                <div class="btn-secondary" @click="close">{{ $t('dash.button.close') }}</div>
            </template>
            <template v-else-if="view === 'values'">
                <div class="btn-secondary" :class="{disable: savingValues}" @click="confirmValues">{{ $t('dash.post-creator.action.apply') }}</div>
                <div class="btn-secondary" @click="close">{{ $t('dash.button.close') }}</div>
            </template>
        </div>

    </div>
</template>

<script>
import VariablesMixin from '@/components/mixins/Variables.vue'
import NotifMixin from '@/components/mixins/Notif.vue'
import BulkApplyVariableValue from '@/components/dash/variables/BulkApplyVariableValue.vue'

export default {
    components: { BulkApplyVariableValue },

    mixins: [VariablesMixin, NotifMixin],

    props: {
        position: {
            type: String,
            default: 'left'
        },

        apply: {
            type: Function,
            default: null
        },

        currentVariableId: {
            type: String,
            default: null
        },
    },

    data() {
        return {
            view: 'list', // 'list' | 'create' | 'values'
            textSearch: '',
            editingVariableId: null,
            newVariableName: '',
            formError: null,
            submitting: false,
            selectedVariable: null,
            valuesDraft: [],
            savingValues: false,
            bulkApply: null, // { value, defaultPageIds } when the bulk-apply-to-other-pages modal is open
        }
    },

    mounted() {
        this.$store.dispatch('variables/fetchVariables')

        // Ouverte via un clic sur un token {{...}} déjà présent dans le texte : on rouvre
        // directement l'étape "valeurs" pour cette variable (pratique si de nouvelles pages ont
        // été ajoutées depuis l'insertion), avec un retour possible vers la liste pour en choisir
        // une autre.
        const editedVariable = this.currentVariableId && this.allVariables.find(v => v.id === this.currentVariableId)
        if (editedVariable) {
            this.selectedVariable = editedVariable
            this.valuesDraft = this.pagesSelected.map(p => ({ page: p, value: this.getValue(editedVariable.id, p.id) }))
            this.view = 'values'
            return
        }

        this.$nextTick(() => {
            if (this.$refs.inputSearch) this.$refs.inputSearch.focus()
        })
    },

    computed: {
        allPages() {
            return this.$store.getters['selectPage/selectPageAllPages'];
        },

        pagesSelected() {
            return this.$store.getters['selectPage/selectPagePageSelected'].map(id => this.allPages[id]).filter(Boolean);
        },

        allVariables() {
            return this.$store.getters['variables/getVariablesList']
        },

        filteredVariables() {
            const q = this.textSearch.trim().toLowerCase()
            if (!q) return this.allVariables
            return this.allVariables.filter(variable => variable.name.toLowerCase().includes(q))
        },

        storedValues() {
            return this.$store.getters['variables/getValues']
        },

        // dès qu'une page de la liste n'a pas de valeur, on propose de dupliquer une
        // valeur déjà renseignée vers les autres pages (via la popup de duplication)
        hasAnyMissingValue() {
            return this.valuesDraft.some(row => !row.value)
        },
    },

    watch: {
        view(newView) {
            if (newView === 'create') {
                this.$nextTick(() => { if (this.$refs.inputCreate) this.$refs.inputCreate.focus() })
            }
        }
    },

    methods: {
        getValue(variableId, pageId) {
            return (this.storedValues[variableId] && this.storedValues[variableId][pageId]) || ''
        },

        close() {
            this.$emit('close')
        },

        backToList() {
            this.view = 'list'
            this.editingVariableId = null
            this.selectedVariable = null
            this.formError = null
        },

        openCreateForm() {
            this.editingVariableId = null
            this.newVariableName = ''
            this.formError = null
            this.view = 'create'
        },

        openEditForm(variable) {
            this.editingVariableId = variable.id
            this.newVariableName = variable.name
            this.formError = null
            this.view = 'create'
        },

        async onDelete(variable) {
            if (!confirm(this.$t('dash.settings.variables.delete-confirm'))) return
            await this.deleteVariable(variable.id)
            await this.$store.dispatch('variables/fetchVariables')
        },

        async submitVariable() {
            const name = this.newVariableName.trim()
            if (!name || this.submitting) return

            this.submitting = true
            this.formError = null

            const res = this.editingVariableId
                ? await this.updateVariable({ id: this.editingVariableId, name })
                : await this.newVariable(name)

            this.submitting = false

            if (!res || !res.body || !res.body.success) {
                this.formError = res && res.body && res.body.error === 'dash.error.custom_variable_name_already_exists'
                    ? this.$t('dash.settings.variables.error-duplicate')
                    : this.$t('dash.settings.variables.error-invalid')
                return
            }

            await this.$store.dispatch('variables/fetchVariables')

            if (this.editingVariableId) {
                this.backToList()
            } else {
                // nouvelle variable : on enchaîne directement sur le renseignement des valeurs
                // pour les pages sélectionnées, sans quitter le post creator.
                this.selectVariable(res.body.variable)
            }
        },

        selectVariable(variable) {
            this.selectedVariable = variable
            const missing = this.pagesSelected.filter(p => !this.getValue(variable.id, p.id))

            if (!this.pagesSelected.length || !missing.length) {
                this.applyVariable()
                return
            }

            this.valuesDraft = this.pagesSelected.map(p => ({ page: p, value: this.getValue(variable.id, p.id) }))
            this.view = 'values'
        },

        applyVariable() {
            if (this.apply && this.selectedVariable) this.apply(this.selectedVariable)
            this.$emit('close')
        },

        async confirmValues() {
            if (this.savingValues) return
            this.savingValues = true

            const results = await Promise.all(this.valuesDraft.map(async row => {
                const current = this.getValue(this.selectedVariable.id, row.page.id)
                if (row.value === current) return null
                return this.saveVariableValue(this.selectedVariable.id, row.page.id, row.value)
            }))

            await this.$store.dispatch('variables/fetchVariables')
            this.savingValues = false

            if (results.some(res => res && (!res.body || !res.body.success))) {
                this.notify({ text: 'dash.settings.variables.value-save-failed', color: 'danger' })
                return
            }

            this.applyVariable()
        },

        openBulkApplyFromRow(row) {
            this.bulkApply = { value: row.value, defaultPageIds: [row.page.id] }
        },

        onBulkApplied() {
            // recharge les valeurs de la liste depuis le store (fraîchement rafraîchi par
            // la popup de duplication) pour refléter les pages qui viennent d'être servies
            this.valuesDraft = this.valuesDraft.map(row => ({
                page: row.page,
                value: this.getValue(this.selectedVariable.id, row.page.id)
            }))
        },
    },
}
</script>
